import React, { useState, useEffect, useMemo, Suspense } from "react";
import { useLocation } from "react-router-dom";
import {
  Drawer,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import useStore from "hooks/useStore";

const ModalDrawer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [requiresConfirmation, setRequiresConfirmation] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const openModal = useStore((state) => state.openModal);
  const clearModal = useStore((state) => state.clearModal);
  const location = useLocation();
  const pagePath =
    openModal?.pagePath || location.pathname.split("/").pop() + "/Form";
  const modalWidth = openModal?.properties?.modalWidth;

  // ✅ Memoize the lazy component so it doesn't get recreated on every re-render
  const ModalContents = useMemo(() => {
    if (!openModal) return null;
    return React.lazy(() => import(`../../pages/${pagePath}.js`));
  }, [openModal, pagePath]);

  const handleDrawerClose = () => {
    if (requiresConfirmation) {
      setShowConfirmDialog(true);
    } else {
      clearModal();
    }
  };

  const handleCloseConfirmDialog = (confirm) => {
    setShowConfirmDialog(false);
    if (confirm) {
      clearModal();
    }
  };

  const renderModalContents = () => {
    if (!ModalContents) return null;
    const { modalWidth, ...modalProps } = openModal?.properties || {};
    return (
      <ModalContents
        close={clearModal}
        {...modalProps}
        setRequiresConfirmation={setRequiresConfirmation}
      />
    );
  };

  useEffect(() => {
    setRequiresConfirmation(false);
  }, [openModal]);

  return (
    <>
      <Drawer
        anchor="right"
        open={Boolean(openModal)}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: isMobile ? "100vw" : modalWidth || 550,
            maxWidth: "100vw",
          },
        }}
        ModalProps={{ keepMounted: true }}
      >
        <Suspense fallback={<div>Loading...</div>}>
          {renderModalContents()}
        </Suspense>
      </Drawer>
      <Dialog
        open={showConfirmDialog}
        onClose={() => handleCloseConfirmDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Close"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to close this form? Any unsaved changes will
            be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleCloseConfirmDialog(false)}
            color="primary"
          >
            No
          </Button>
          <Button
            onClick={() => handleCloseConfirmDialog(true)}
            color="primary"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalDrawer;
